import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';

import { Rect, useElementRect } from '@paragon/client-tools';

function withMargin(rect: Rect, margin: number): Rect {
  return {
    x: rect.x,
    y: rect.y,

    left: rect.left - margin,
    right: rect.right + margin,
    top: rect.top - margin,
    bottom: rect.bottom + margin,

    width: rect.width + margin * 2,
    height: rect.height + margin * 2,
  };
}

/**
 * Renders a backdrop with a hole in it surrounding the given element. The provided `sx` will be applied to the
 *   backdrop elements.
 */
export function HoleyBackdrop(props: { surroundingElement: HTMLElement | null; margin?: number; sx: SxProps<Theme> }) {
  let hole = useElementRect(props.surroundingElement);
  if (hole == null) return null;
  if (props.margin) hole = withMargin(hole, props.margin);

  const commonOverlaySx = {
    position: 'fixed',
    padding: 0,
    margin: 0,
  } as const;

  const backdropSx = {
    ...props.sx,
    ...commonOverlaySx,
  } as const;

  const holeSx = {
    // don't apply provided sx w/ filters to the hole
    ...commonOverlaySx,
  };

  return (
    <>
      <Box
        // top left corner
        sx={{
          ...backdropSx,
          top: 0,
          left: 0,
          width: hole.left,
          height: hole.top,
        }}
      />
      <Box
        // top
        sx={{
          ...backdropSx,
          top: 0,
          left: hole.left,
          width: hole.width,
          height: hole.top,
        }}
      />
      <Box
        // top-right corner
        sx={{
          ...backdropSx,
          top: 0,
          left: hole.right,
          width: window.innerWidth - hole.right,
          height: hole.top,
        }}
      />

      <Box
        // right
        sx={{
          ...backdropSx,
          top: hole.top,
          left: hole.right,
          width: window.innerWidth - hole.right,
          height: hole.height,
        }}
      />

      <Box
        // bottom-right corner
        sx={{
          ...backdropSx,
          top: hole.bottom,
          left: hole.right,
          width: window.innerWidth - hole.right,
          height: window.innerHeight - hole.bottom,
        }}
      />

      <Box
        // bottom
        sx={{
          ...backdropSx,
          top: hole.bottom,
          left: hole.left,
          width: hole.width,
          height: window.innerHeight - hole.bottom,
        }}
      />

      <Box
        // bottom-left corner
        sx={{
          ...backdropSx,
          top: hole.bottom,
          left: 0,
          width: hole.left,
          height: window.innerHeight - hole.bottom,
        }}
      />

      <Box
        // left
        sx={{
          ...backdropSx,
          top: hole.top,
          left: 0,
          width: hole.left,
          height: hole.height,
        }}
      />

      <Box
        // middle
        sx={{
          ...holeSx,
          top: hole.top,
          left: hole.left,
          width: hole.width,
          height: hole.height,
        }}
      />
    </>
  );
}
